import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Drawer,
  IconButton,
  InputAdornment,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CreateNewFolderRoundedIcon from '@mui/icons-material/CreateNewFolderRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import PhotoLibraryRoundedIcon from '@mui/icons-material/PhotoLibraryRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import { useAuth } from '@hooks/auth/useAuth';
import { useOrg } from '@hooks/auth/useOrg';
import { useStorageScope } from '@hooks/auth/useStorageScope';
import { useFileList } from '@hooks/drive/useFileList';
import { useUploadQueue } from '@hooks/upload/useUploadQueue';
import {
  bulkDeleteFiles,
  bulkMoveFiles,
  configureConnectionCors,
  createAdminConnection,
  createFolder,
  deleteAdminConnection,
  deleteFile,
  downloadFile,
  getCurrentSession,
  listConnections,
  listOrgConnections,
  moveFile,
  updateAdminConnection,
} from '@services/api/r2ApiClient';
import { createEmbedShare, listEmbedShares } from '@services/api/embedApiClient';
import {
  clearActiveConnectionId,
  getActiveConnectionId,
  getActiveOrgId,
  setActiveConnectionId,
  setActiveOrgId,
} from '@services/storage/savedConnectionsStorage';
import { joinPath, getParentPath } from '@utils/helpers/pathHelpers';
import {
  buildEmbedShareInfo,
  buildPublicDriveEmbedUrl,
  embedViewTypeForViewerMode,
  presentationViewForViewerMode,
} from '@utils/helpers/embedHelpers';
import { probeViewerHealth, type ViewerHealth } from '@utils/helpers/viewerHealth';
import { collectDroppedUpload, type DroppedUploadFile } from '@utils/helpers/dropUpload';
import { AdminPanel } from '@components/AdminPanel';
import { ConnectionDialog } from '@components/ConnectionDialog';
import { EmbedDialog, type EmbedTarget } from '@components/EmbedDialog';
import { DriveItemRow } from '@components/drive/DriveItemRow';
import type { DriveItemAction } from '@components/drive/DriveItemMenu';
import { DeleteConfirmDialog } from '@components/drive/DeleteConfirmDialog';
import { RenameDialog } from '@components/drive/RenameDialog';
import { MoveDialog } from '@components/drive/MoveDialog';
import { CreateFolderDialog } from '@components/drive/CreateFolderDialog';
import { FilePropertiesDialog } from '@components/drive/FilePropertiesDialog';
import { BulkActionToolbar } from '@components/drive/BulkActionToolbar';
import { ViewerPanel } from '@/viewer/ViewerPanel';
import { shouldUseInlineViewer } from '@config/featureFlags';
import type { ConnectionPermission, SavedConnection } from '@/types/connection.types';
import type { EmbedShare, EmbedShareInfo, EmbedViewType } from '@/types/embed.types';
import type { DriveItem } from '@/types/file.types';
import type { ViewerMode, ViewerState } from '@genesisframeworks/viewer-plugin';

interface InlineViewerState {
  folderPath: string;
  folderName: string;
  filePath?: string;
  contentType: 'image' | 'pdf';
  /** Explicit mode selected by the Drive action that opened the drawer. */
  initialMode?: ViewerMode;
}

type DriveView = 'files' | 'bookmarks' | 'transfers' | 'admin';

const APPROVAL_POLL_INTERVAL_MS = 10_000;

const PROTOCOL_LABELS: Record<SavedConnection['protocol'], string> = {
  s3: 'S3',
  r2: 'R2',
  'dreamhost-object': 'DreamHost',
  minio: 'MinIO',
  sftp: 'SFTP',
};

function isFile(item: DriveItem): item is Extract<DriveItem, { type: 'file' }> {
  return item.type === 'file';
}

function isPdfPath(path: string): boolean {
  return path.toLowerCase().endsWith('.pdf');
}

function normalizeDrivePath(value = ''): string {
  return String(value || '').replace(/\\/g, '/').replace(/^\/+|\/+$/g, '').split('/').filter(Boolean).join('/');
}

function normalizeGrantPrefix(value = ''): string {
  const path = normalizeDrivePath(value);
  return path ? `${path}/` : '';
}

function eventHasFiles(event: DragEvent<HTMLElement>): boolean {
  return Array.from(event.dataTransfer?.types || []).includes('Files');
}

function pathWithinGrantPrefix(path: string, prefix: string): boolean {
  const normalizedPath = normalizeDrivePath(path);
  const normalizedPrefix = normalizeGrantPrefix(prefix);
  if (!normalizedPrefix) return true;
  const barePrefix = normalizedPrefix.replace(/\/$/, '');
  return normalizedPath === barePrefix || normalizedPath.startsWith(normalizedPrefix);
}

function connectionCanAccess(
  connection: SavedConnection | null,
  hasFullDriveAccess: boolean,
  path: string,
  permission: ConnectionPermission,
): boolean {
  if (!connection) return false;
  if (hasFullDriveAccess) return true;
  const grant = connection?.connectionGrant;
  if (!grant?.permissions?.includes(permission)) return false;
  return (grant.allowedPrefixes || ['']).some((prefix) => pathWithinGrantPrefix(path, prefix));
}

function connectionHasPermission(
  connection: SavedConnection | null,
  hasFullDriveAccess: boolean,
  permission: ConnectionPermission,
): boolean {
  if (!connection) return false;
  return hasFullDriveAccess || !!connection.connectionGrant?.permissions?.includes(permission);
}

export function DrivePage() {
  const navigate = useNavigate();
  const { items, loading, error, fetchFiles, clearFiles } = useFileList();
  const { enqueue, tasks } = useUploadQueue();
  const { user, token, login, logout } = useAuth();
  const { org, setOrg } = useOrg();
  const { scope: activeStorageScope, setScope: setActiveStorageScope } = useStorageScope();
  const [connections, setConnections] = useState<SavedConnection[]>([]);
  const [activeConnection, setActiveConnection] = useState<SavedConnection | null>(null);
  const [pendingOrgId, setPendingOrgId] = useState<string | null>(null);
  const [approvalRefreshBusy, setApprovalRefreshBusy] = useState(false);
  const [approvalRefreshError, setApprovalRefreshError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<DriveView>('files');
  const [currentPath, setCurrentPath] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConnection, setEditingConnection] = useState<SavedConnection | null>(null);
  const [query, setQuery] = useState('');
  const [viewerError, setViewerError] = useState<string | null>(null);
  const [embedShares, setEmbedShares] = useState<EmbedShare[]>([]);
  const [embedTarget, setEmbedTarget] = useState<EmbedTarget | null>(null);
  const [embedDialogOpen, setEmbedDialogOpen] = useState(false);
  const [inlineViewer, setInlineViewer] = useState<InlineViewerState | null>(null);
  // Live public file CORS health per connection (HEAD probe of publicUrl root) — connectionId
  // → ViewerHealth. Probed when the Bookmarks view mounts and re-probed
  // after any save. Drives the "CORS ✓ / ✗" chip on each connection card.
  const [viewerHealthMap, setViewerHealthMap] = useState<Record<string, ViewerHealth>>({});
  const [corsConfiguringId, setCorsConfiguringId] = useState<string | null>(null);
  // ─── Drive management state (per-item kebab menu + multi-select) ───────
  /** Set of currently selected item paths (multi-select mode). */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** Item targeted by a single-item dialog (rename / move / delete / props). */
  const [actionTarget, setActionTarget] = useState<DriveItem | null>(null);
  /** Which dialog is open. */
  const [openDialog, setOpenDialog] = useState<null | 'rename' | 'move' | 'delete' | 'properties'>(null);
  /** Bulk dialog flavor — distinct so a single-item delete doesn't show "N items". */
  const [openBulkDialog, setOpenBulkDialog] = useState<null | 'delete' | 'move'>(null);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [actionToast, setActionToast] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);
  const theme = useTheme();
  const isMobileDrawer = useMediaQuery(theme.breakpoints.down('sm'));
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dragDepthRef = useRef(0);
  const shareInfoCacheRef = useRef<Map<string, EmbedShareInfo>>(new Map());
  const sessionRequestIdRef = useRef(0);
  const connectionRequestIdRef = useRef(0);
  const latestSessionRef = useRef<{ orgId: string; isAdmin: boolean }>({ orgId: '', isAdmin: false });
  const latestApprovalRef = useRef(!!org?.isApproved);
  const isLocalDevAdmin = useMemo(() => {
    if (!token?.startsWith('basic:')) return false;
    const host = window.location.hostname;
    return host === 'localhost' || host === '127.0.0.1' || host === '::1';
  }, [token]);
  const isPlatformOwner = !!org?.isAdmin || org?.platformRole === 'platform_owner' || isLocalDevAdmin;
  const isOrgAdmin = !!org?.isApproved && (org?.role === 'admin' || org?.role === 'owner');
  const isApprovalPending = !!org && !org.isApproved && !isPlatformOwner;
  const hasFullDriveAccess = isPlatformOwner || isOrgAdmin;
  const canManageMembers = isPlatformOwner || !!org?.canManageMembers || isOrgAdmin;
  const orgOptions = useMemo(() => {
    if (org?.orgs?.length) return org.orgs;
    if (!org) return [];
    return [{
      id: org.id,
      name: org.name,
      role: org.role,
      storageBackend: org.storageBackend,
      memberCount: org.memberCount,
      isApproved: org.isApproved,
      status: org.status,
    }];
  }, [org]);
  const selectedOrgId = useMemo(() => {
    const value = pendingOrgId || org?.id || '';
    return orgOptions.some((option) => option.id === value) ? value : '';
  }, [org?.id, orgOptions, pendingOrgId]);
  // The org whose storages are currently loaded. Each org is rendered as a
  // selectable row in the header switcher, with that org's storages listed
  // beneath it as "Org · Storage" so two storages in one org are
  // distinguishable.
  const currentOrgId = selectedOrgId || org?.id || '';
  latestSessionRef.current = { orgId: currentOrgId, isAdmin: isPlatformOwner };
  // Header switcher is worth showing when there is more than one storage to
  // pick from OR more than one org to switch between.
  const showHeaderSwitcher = connections.length > 1 || orgOptions.length > 1;
  // Selected value for the switcher. Values are prefixed so one <Select> routes
  // both storage picks ("conn:<id>") and org switches ("org:<id>"). Falls back
  // to the current org row when no specific storage is active, so the field
  // always shows the active context and never triggers MUI out-of-range warnings.
  const headerSelectValue = activeConnection && connections.some((c) => c.id === activeConnection.id)
    ? `conn:${activeConnection.id}`
    : (currentOrgId ? `org:${currentOrgId}` : '');
  const canWriteInCurrentPath = connectionCanAccess(activeConnection, hasFullDriveAccess, currentPath, 'write');
  const canReadInCurrentPath = connectionCanAccess(activeConnection, hasFullDriveAccess, currentPath, 'read');
  const canWriteSomewhere = connectionHasPermission(activeConnection, hasFullDriveAccess, 'write');

  const syncConnections = useCallback(async (options: { orgId?: string; admin?: boolean } = {}) => {
    const requestId = ++connectionRequestIdRef.current;
    const latestSession = latestSessionRef.current;
    const targetOrgId = options.orgId || currentOrgId || latestSession.orgId || getActiveOrgId() || '';
    const shouldUseAdminInventory = options.admin ?? (latestSession.isAdmin || isPlatformOwner);
    const next = shouldUseAdminInventory && targetOrgId
      ? await listOrgConnections(targetOrgId)
      : await listConnections();
    if (requestId !== connectionRequestIdRef.current) return false;
    setConnections(next);
    const activeId = getActiveConnectionId(targetOrgId);
    const matched = activeId ? next.find((connection) => connection.id === activeId) || null : null;
    if (matched) {
      setActiveConnection(matched);
      setActiveStorageScope({ orgId: targetOrgId, connectionId: matched.id });
    } else if (next[0]) {
      setActiveConnectionId(next[0].id, targetOrgId);
      setActiveConnection(next[0]);
      setActiveStorageScope({ orgId: targetOrgId, connectionId: next[0].id });
    } else {
      clearActiveConnectionId(targetOrgId);
      setActiveConnection(null);
      setActiveStorageScope(null);
    }
    return true;
  }, [currentOrgId, isPlatformOwner, setActiveStorageScope]);

  const refreshEmbedShares = useCallback(async () => {
    if (!activeStorageScope) {
      setEmbedShares([]);
      return;
    }
    try {
      setEmbedShares(await listEmbedShares(undefined, activeStorageScope));
      shareInfoCacheRef.current.clear();
    } catch (err) {
      console.warn('[Embeds] Unable to load shares', err);
      setEmbedShares([]);
    }
  }, [activeStorageScope]);

  const loadPath = useCallback(async (path = '') => {
    // A selection only belongs to the currently displayed folder and storage.
    // Clear it before any navigation/refetch so bulk actions can never carry
    // stale paths into a newly selected connection.
    setSelected(new Set());
    if (!activeStorageScope) {
      clearFiles();
      return;
    }
    setCurrentPath(path);
    await fetchFiles(path, activeStorageScope);
    await refreshEmbedShares();
  }, [activeStorageScope, clearFiles, fetchFiles, refreshEmbedShares]);

  const refreshSession = useCallback(async (options: { expectedOrgId?: string } = {}) => {
    const requestId = ++sessionRequestIdRef.current;
    let data;
    try {
      data = await getCurrentSession();
    } catch (err) {
      if (requestId !== sessionRequestIdRef.current) return false;
      throw err;
    }
    if (requestId !== sessionRequestIdRef.current) return false;
    if (options.expectedOrgId && data.org.id !== options.expectedOrgId) {
      throw new Error('Unable to switch organization');
    }
    if (data.token && data.token !== token) {
      await login(data.token, '');
    }
    setOrg({
      id: data.org.id,
      name: data.org.name,
      role: data.org.role,
      storageBackend: data.org.storageBackend || 'r2',
      memberCount: data.org.memberCount,
      isApproved: !!data.org.isApproved,
      status: data.org.status || 'pending',
      isAdmin: !!data.org.isAdmin,
      adminEnabled: !!data.org.adminEnabled,
      platformRole: data.org.platformRole === 'platform_owner' ? 'platform_owner' : null,
      canManageMembers: !!data.org.canManageMembers,
      orgs: data.orgs || [],
    });
    latestApprovalRef.current = !!data.org.isApproved;
    setActiveOrgId(data.org.id);
    latestSessionRef.current = {
      orgId: data.org.id,
      isAdmin: data.org.platformRole === 'platform_owner' || !!data.org.isAdmin || isLocalDevAdmin,
    };
    setPendingOrgId((current) => (current === data.org.id ? null : current));
    return true;
  }, [isLocalDevAdmin, login, setOrg, token]);

  useEffect(() => {
    void refreshSession()
      .then((didCommit) => (didCommit ? syncConnections() : undefined))
      .catch(() => {});
  }, [refreshSession, syncConnections]);

  const checkApprovalStatus = useCallback(async (showFeedback = false) => {
    if (showFeedback) {
      setApprovalRefreshBusy(true);
      setApprovalRefreshError(null);
    }
    try {
      const didCommit = await refreshSession();
      if (didCommit && latestApprovalRef.current) {
        await syncConnections();
      }
    } catch (err) {
      if (showFeedback) {
        setApprovalRefreshError((err as Error).message || 'Unable to check approval status.');
      }
    } finally {
      if (showFeedback) setApprovalRefreshBusy(false);
    }
  }, [refreshSession, syncConnections]);

  useEffect(() => {
    if (!isApprovalPending) return;

    const intervalId = window.setInterval(() => {
      void checkApprovalStatus();
    }, APPROVAL_POLL_INTERVAL_MS);
    const handleFocus = () => {
      void checkApprovalStatus();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
  }, [checkApprovalStatus, isApprovalPending]);

  // Probe View CORS health for every connection with publicUrl whenever the
  // connections list changes (load, save, redeploy). Each probe is a HEAD
  // request to {publicUrl}/ and runs in parallel. Results live
  // in viewerHealthMap keyed by connection.id. Heuristic — see viewerHealth.ts.
  useEffect(() => {
    const targets = connections.filter((c) => c.publicUrl);
    if (targets.length === 0) return;
    let cancelled = false;
    void Promise.all(targets.map(async (conn) => {
      const health = await probeViewerHealth(conn.publicUrl as string);
      if (cancelled) return null;
      return [conn.id, health] as const;
    })).then((entries) => {
      if (cancelled) return;
      const next: Record<string, ViewerHealth> = {};
      for (const e of entries) if (e) next[e[0]] = e[1];
      setViewerHealthMap(next);
    });
    return () => { cancelled = true; };
  }, [connections]);

  useEffect(() => {
    if (!activeConnection?.id) {
      clearFiles();
      return;
    }
    if (!org?.isApproved && !isPlatformOwner) return;
    void loadPath('');
  }, [activeConnection?.id, activeConnection?.protocol, clearFiles, isPlatformOwner, org?.isApproved, loadPath]);

  const handleSaveConnection = useCallback(async (connection: SavedConnection) => {
    const saved = editingConnection
      ? await updateAdminConnection(editingConnection.id, connection)
      : await createAdminConnection(connection);
    setActiveConnectionId(saved.id, currentOrgId);
    await syncConnections();
    setDialogOpen(false);
    setEditingConnection(null);
    setActiveView('files');
  }, [currentOrgId, editingConnection, syncConnections]);

  // ─── Drive item actions ───────────────────────────────────────────────
  const showToast = (severity: 'success' | 'error', message: string) => {
    setActionToast({ severity, message });
    setTimeout(() => setActionToast(null), 4000);
  };

  const handleConfigureCors = async (connection: SavedConnection) => {
    setCorsConfiguringId(connection.id);
    try {
      await configureConnectionCors({ orgId: currentOrgId, connectionId: connection.id });
      showToast('success', 'Browser upload CORS enabled. Storage changes can take up to 30 seconds to propagate.');
    } catch (error) {
      showToast('error', error instanceof Error ? error.message : 'Unable to configure browser upload CORS');
    } finally {
      setCorsConfiguringId(null);
    }
  };

  const toggleSelected = useCallback((path: string) => {
    setSelected((curr) => {
      const next = new Set(curr);
      if (next.has(path)) next.delete(path); else next.add(path);
      return next;
    });
  }, []);
  const clearSelection = useCallback(() => setSelected(new Set()), []);

  const handleSingleRename = useCallback(async (newPath: string) => {
    if (!actionTarget) return;
    setActionBusy(true);
    try {
      if (!activeStorageScope) throw new Error('Select an assigned connection first');
      await moveFile(actionTarget.path, newPath, activeStorageScope);
      await loadPath(currentPath);
      setOpenDialog(null);
      setActionTarget(null);
      showToast('success', 'Renamed.');
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Rename failed');
    } finally {
      setActionBusy(false);
    }
  }, [actionTarget, activeStorageScope, currentPath, loadPath]);

  const handleSingleMove = useCallback(async (targetFolder: string) => {
    if (!actionTarget) return;
    setActionBusy(true);
    try {
      const idx = actionTarget.path.lastIndexOf('/');
      const baseName = idx >= 0 ? actionTarget.path.slice(idx + 1) : actionTarget.path;
      const dest = targetFolder ? `${targetFolder}/${baseName}` : baseName;
      if (!activeStorageScope) throw new Error('Select an assigned connection first');
      await moveFile(actionTarget.path, dest, activeStorageScope);
      await loadPath(currentPath);
      setOpenDialog(null);
      setActionTarget(null);
      showToast('success', `Moved to /${targetFolder || '(root)'}`);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Move failed');
    } finally {
      setActionBusy(false);
    }
  }, [actionTarget, activeStorageScope, currentPath, loadPath]);

  const handleSingleDelete = useCallback(async () => {
    if (!actionTarget) return;
    setActionBusy(true);
    try {
      if (!activeStorageScope) throw new Error('Select an assigned connection first');
      await deleteFile(actionTarget.path, activeStorageScope);
      await loadPath(currentPath);
      setOpenDialog(null);
      setActionTarget(null);
      showToast('success', 'Deleted.');
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setActionBusy(false);
    }
  }, [actionTarget, activeStorageScope, currentPath, loadPath]);

  const handleBulkDelete = useCallback(async () => {
    const paths = [...selected];
    if (paths.length === 0) return;
    setActionBusy(true);
    try {
      if (!activeStorageScope) throw new Error('Select an assigned connection first');
      const res = await bulkDeleteFiles(paths, activeStorageScope);
      await loadPath(currentPath);
      setSelected(new Set());
      setOpenBulkDialog(null);
      const sev = res.errors > 0 ? 'error' : 'success';
      const msg = res.errors > 0
        ? `Deleted ${res.deleted} of ${paths.length}, ${res.errors} failed`
        : `Deleted ${res.deleted} ${res.deleted === 1 ? 'item' : 'items'}.`;
      showToast(sev, msg);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Bulk delete failed');
    } finally {
      setActionBusy(false);
    }
  }, [selected, activeStorageScope, currentPath, loadPath]);

  const handleBulkMove = useCallback(async (targetFolder: string) => {
    const paths = [...selected];
    if (paths.length === 0) return;
    setActionBusy(true);
    try {
      const moves = paths.map((src) => {
        const idx = src.lastIndexOf('/');
        const baseName = idx >= 0 ? src.slice(idx + 1) : src;
        const dest = targetFolder ? `${targetFolder}/${baseName}` : baseName;
        return { from: src, to: dest };
      });
      if (!activeStorageScope) throw new Error('Select an assigned connection first');
      const res = await bulkMoveFiles(moves, activeStorageScope);
      await loadPath(currentPath);
      setSelected(new Set());
      setOpenBulkDialog(null);
      const sev = res.errors > 0 ? 'error' : 'success';
      const msg = res.errors > 0
        ? `Moved ${res.moved} of ${paths.length}, ${res.errors} failed`
        : `Moved ${res.moved} ${res.moved === 1 ? 'item' : 'items'} to /${targetFolder || '(root)'}.`;
      showToast(sev, msg);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Bulk move failed');
    } finally {
      setActionBusy(false);
    }
  }, [selected, activeStorageScope, currentPath, loadPath]);

  const handleOpenConnection = useCallback((connection: SavedConnection) => {
    clearSelection();
    setActiveConnectionId(connection.id, currentOrgId);
    setActiveConnection(connection);
    setActiveStorageScope({ orgId: currentOrgId, connectionId: connection.id });
    setActiveView('files');
  }, [clearSelection, currentOrgId, setActiveStorageScope]);

  const handleOrgChange = useCallback(async (nextOrgId: string) => {
    if (!nextOrgId || (nextOrgId === org?.id && !pendingOrgId)) return;
    setPendingOrgId(nextOrgId);
    setActiveOrgId(nextOrgId);
    connectionRequestIdRef.current += 1;
    setActiveConnection(null);
    setActiveStorageScope(null);
    setConnections([]);
    clearSelection();
    clearFiles();
    setCurrentPath('');
    setEmbedShares([]);
    shareInfoCacheRef.current.clear();
    setActiveView('files');
    try {
      const didCommit = await refreshSession({ expectedOrgId: nextOrgId });
      if (didCommit) {
        await syncConnections({ orgId: nextOrgId, admin: isPlatformOwner });
      }
    } catch (err) {
      if (org?.id) {
        setActiveOrgId(org.id);
      }
      setPendingOrgId((current) => (current === nextOrgId ? null : current));
      showToast('error', err instanceof Error ? err.message : 'Unable to switch organization');
    } finally {
      setPendingOrgId((current) => (current === nextOrgId ? null : current));
    }
  }, [clearFiles, clearSelection, isPlatformOwner, org?.id, pendingOrgId, refreshSession, setActiveStorageScope, syncConnections]);

  const handleDeleteConnection = useCallback(async (connection: SavedConnection) => {
    await deleteAdminConnection(connection.id);
    await syncConnections();
  }, [syncConnections]);

  // Routes a header-switcher selection: "conn:<id>" activates a storage in the
  // current org; "org:<id>" switches to another org, or — if it's the org
  // already loaded — drops back to that org's default drive.
  const handleHeaderSelect = useCallback((raw: string) => {
    if (raw.startsWith('conn:')) {
      const connection = connections.find((c) => c.id === raw.slice(5));
      if (connection) handleOpenConnection(connection);
    } else if (raw.startsWith('org:')) {
      const nextOrgId = raw.slice(4);
      if (nextOrgId !== currentOrgId) {
        void handleOrgChange(nextOrgId);
      }
    }
  }, [connections, currentOrgId, handleOpenConnection, handleOrgChange]);

  const handleUploadClick = useCallback(() => {
    if (!canWriteInCurrentPath) {
      showToast('error', activeConnection
        ? 'You do not have upload access for this folder.'
        : 'Select an assigned connection before uploading.');
      return;
    }
    fileInputRef.current?.click();
  }, [activeConnection, canWriteInCurrentPath]);

  const uploadItemsToCurrentPath = useCallback(async (
    files: DroppedUploadFile[],
    directories: string[] = [],
  ) => {
    if (files.length === 0 && directories.length === 0) return;
    if (!canWriteInCurrentPath || !activeConnection || !currentOrgId) {
      showToast('error', activeConnection
        ? 'You do not have upload access for this folder.'
        : 'Select an assigned connection before uploading.');
      return;
    }
    const storageScope = {
      orgId: currentOrgId,
      connectionId: activeConnection.id,
    };

    let foldersCreated = 0;
    let foldersFailed = 0;
    for (const directory of directories) {
      try {
        await createFolder(joinPath(currentPath, directory), storageScope);
        foldersCreated += 1;
      } catch {
        foldersFailed += 1;
      }
    }

    let succeeded = 0;
    let failed = 0;
    let delivered = 0;
    let publicationPending = 0;
    let publicationFailed = 0;
    for (const upload of files) {
      const result = await enqueue(upload.file, joinPath(currentPath, upload.relativePath), storageScope);
      if (result.success) {
        succeeded += 1;
        if (result.publicationStatus === 'delivered') delivered += 1;
        if (result.publicationStatus === 'pending') publicationPending += 1;
        if (result.publicationStatus === 'failed') publicationFailed += 1;
      } else {
        failed += 1;
      }
    }
    await loadPath(currentPath);
    const totalFailed = failed + foldersFailed;
    const folderSummary = foldersCreated > 0
      ? ` Created ${foldersCreated} folder${foldersCreated === 1 ? '' : 's'}.`
      : '';
    if (totalFailed > 0) {
      showToast(
        'error',
        `Uploaded ${succeeded} of ${files.length} file${files.length === 1 ? '' : 's'}; ${totalFailed} item${totalFailed === 1 ? '' : 's'} failed.`,
      );
    } else if (publicationFailed > 0) {
      showToast('error', `Uploaded ${succeeded} file${succeeded > 1 ? 's' : ''}, but ${publicationFailed} change notification${publicationFailed > 1 ? 's' : ''} could not be recorded.`);
    } else if (publicationPending > 0) {
      showToast('error', `Uploaded ${succeeded} file${succeeded > 1 ? 's' : ''}. ${publicationPending} integration update${publicationPending > 1 ? 's are' : ' is'} retrying.`);
    } else if (succeeded > 0 && failed === 0) {
      const suffix = delivered > 0 ? ' Integrations updated.' : '';
      showToast('success', `Uploaded ${succeeded} file${succeeded === 1 ? '' : 's'}.${folderSummary}${suffix}`);
    } else if (foldersCreated > 0) {
      showToast('success', `Uploaded ${foldersCreated} folder${foldersCreated === 1 ? '' : 's'}.`);
    }
  }, [activeConnection, canWriteInCurrentPath, currentOrgId, currentPath, enqueue, loadPath]);

  const handleCreateFolder = useCallback(async (folderName: string) => {
    setActionBusy(true);
    try {
      const folderPath = joinPath(currentPath, folderName);
      if (!activeStorageScope) throw new Error('Select an assigned connection first');
      const result = await createFolder(folderPath, activeStorageScope);
      await loadPath(currentPath);
      setCreateFolderOpen(false);
      showToast('success', result.share ? 'Folder created. Share link is ready.' : 'Folder created.');
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Create folder failed');
    } finally {
      setActionBusy(false);
    }
  }, [activeStorageScope, currentPath, loadPath]);

  const handleUploadChange = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    await uploadItemsToCurrentPath(files.map((file) => ({
      file,
      relativePath: file.webkitRelativePath || file.name,
    })));
    event.target.value = '';
  }, [uploadItemsToCurrentPath]);

  const handleFileDragEnter = useCallback((event: DragEvent<HTMLElement>) => {
    if (!eventHasFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current += 1;
    event.dataTransfer.dropEffect = canWriteInCurrentPath ? 'copy' : 'none';
    setIsDraggingFiles(true);
  }, [canWriteInCurrentPath]);

  const handleFileDragOver = useCallback((event: DragEvent<HTMLElement>) => {
    if (!eventHasFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = canWriteInCurrentPath ? 'copy' : 'none';
  }, [canWriteInCurrentPath]);

  const handleFileDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
    if (!eventHasFiles(event) && dragDepthRef.current === 0) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDraggingFiles(false);
    }
  }, []);

  const handleFileDrop = useCallback(async (event: DragEvent<HTMLElement>) => {
    if (!eventHasFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setIsDraggingFiles(false);
    try {
      const dropped = await collectDroppedUpload(event.dataTransfer);
      await uploadItemsToCurrentPath(dropped.files, dropped.directories);
    } catch (error) {
      showToast('error', error instanceof Error ? error.message : 'Unable to read the dropped folder.');
    }
  }, [uploadItemsToCurrentPath]);

  const handleDownload = useCallback(async (path: string) => {
    if (!activeStorageScope) throw new Error('Select an assigned connection first');
    const blob = await downloadFile(path, activeStorageScope);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = path.split('/').pop() || 'download';
    link.click();
    URL.revokeObjectURL(url);
  }, [activeStorageScope]);

  const handleOpenPdf = useCallback(async (path: string) => {
    if (!activeStorageScope) {
      showToast('error', 'Select an assigned connection first');
      return;
    }
    setInlineViewer({
      folderPath: getParentPath(path),
      filePath: path,
      folderName: path.split('/').pop() || 'PDF',
      contentType: 'pdf',
      initialMode: 'pdf-reader',
    });
  }, [activeStorageScope]);

  const handleOpenEmbedDialog = useCallback((item: DriveItem, initialViewType?: EmbedTarget['initialViewType']) => {
    if (item.type === 'folder') {
      setEmbedTarget({
        targetType: 'folder',
        folderPath: item.path,
        folderName: item.name,
        initialViewType: initialViewType || 'gallery',
      });
    } else {
      setEmbedTarget({
        targetType: 'file',
        folderPath: getParentPath(item.path),
        folderName: item.name,
        filePath: item.path,
        initialViewType: initialViewType || 'pdf-reader',
      });
    }
    setEmbedDialogOpen(true);
  }, []);

  const handleShareCurrentFolder = useCallback(() => {
    setEmbedTarget({
      targetType: 'folder',
      folderPath: currentPath,
      folderName: currentPath.split('/').filter(Boolean).pop() || activeConnection?.nickname || 'Drive',
      initialViewType: 'gallery',
    });
    setEmbedDialogOpen(true);
  }, [activeConnection?.nickname, currentPath]);

  const getFolderEmbedInfo = useCallback(async ({
    folderPath,
    folderName,
    contentType,
    mode,
    state,
  }: {
    folderPath: string;
    folderName: string;
    contentType: 'image' | 'pdf';
    mode: ViewerMode;
    state: ViewerState;
  }): Promise<EmbedShareInfo> => {
    const viewType: EmbedViewType = embedViewTypeForViewerMode(mode, contentType);
    const presentationView = presentationViewForViewerMode(mode, contentType);
    const publicUrl = activeConnection?.publicUrl || undefined;
    const cacheKey = [
      activeConnection?.id || 'default',
      publicUrl || '',
      folderPath,
      presentationView,
      presentationView === 'multiview' ? state.layout || 'filmstrip' : '',
      state.album || '',
      state.image || '',
      state.book || '',
      state.page || '',
      state.pdfQuality || 'auto',
    ].join('\n');
    const cached = shareInfoCacheRef.current.get(cacheKey);
    if (cached) return cached;

    const existing = embedShares.find((share) => (
      !share.revokedAt
      && (share.targetType || 'folder') === 'folder'
      && share.folderPath === folderPath
    ));

    if (!activeStorageScope) throw new Error('Select an assigned connection first');
    const share = existing || await createEmbedShare({
      folderPath,
      targetType: 'folder',
      title: folderName || 'Drive',
      viewType,
    }, activeStorageScope);

    if (!existing) await refreshEmbedShares();

    const info = buildEmbedShareInfo(share, {
      publicUrl,
      view: presentationView,
      layout: presentationView === 'multiview' ? state.layout || 'filmstrip' : undefined,
      album: state.album,
      image: state.image,
      book: state.book,
      page: state.page,
      quality: state.pdfQuality,
    });
    shareInfoCacheRef.current.set(cacheKey, info);
    return info;
  }, [activeConnection?.id, activeConnection?.publicUrl, activeStorageScope, embedShares, refreshEmbedShares]);

  const openFolderViewer = useCallback(async (
    folderPath: string,
    folderName: string,
    viewType: 'gallery' | 'pdf-library',
  ) => {
    setViewerError(null);

    // OWNER FAST-PATH (PR 2): if the inline viewer is enabled, open the
    // ViewerPanel Drawer directly with /api/files?format=manifest. Skips
    // the share-create round-trip entirely. Sub-100ms target.
    //
    // The legacy share-create+navigate flow is preserved for the
    // ?legacyViewer=1 escape hatch and the OWNER_INLINE_VIEWER=false env.
    if (shouldUseInlineViewer()) {
      setInlineViewer({
        folderPath,
        folderName: folderName || 'Drive',
        contentType: viewType === 'gallery' ? 'image' : 'pdf',
        // Map the Drive action to the plugin's canonical mode.
        initialMode: viewType === 'gallery' ? 'image-gallery' : 'pdf-library',
      });
      return;
    }

    try {
      if (!activeStorageScope) throw new Error('Select an assigned connection first');
      const share = await createEmbedShare({
        folderPath,
        targetType: 'folder',
        title: folderName || 'Drive',
        viewType,
      }, activeStorageScope);
      await refreshEmbedShares();
      const url = new URL(buildPublicDriveEmbedUrl(share));
      navigate(`${url.pathname}${url.search}`);
    } catch (err) {
      setViewerError((err as Error)?.message || 'Unable to open folder viewer');
    }
  }, [activeStorageScope, navigate, refreshEmbedShares]);

  /** Dispatcher for secondary row actions. Primary Preview and Share actions
   * are wired directly by DriveItemRow so the More menu never duplicates them. */
  const handleItemAction = (item: DriveItem, action: DriveItemAction) => {
    if (action === 'library' && item.type === 'folder') {
      void openFolderViewer(item.path, item.name, 'pdf-library');
      return;
    }
    if (action === 'download') {
      void handleDownload(item.path)
        .catch((error) => showToast('error', error instanceof Error ? error.message : 'Download failed'));
      return;
    }
    if (action === 'copy-url') {
      void navigator.clipboard.writeText(item.path)
        .then(() => showToast('success', `Copied: /${item.path}`))
        .catch(() => showToast('error', 'Clipboard unavailable'));
      return;
    }

    setActionTarget(item);
    if (action === 'rename') { setOpenDialog('rename'); return; }
    if (action === 'move') { setOpenDialog('move'); return; }
    if (action === 'delete') { setOpenDialog('delete'); return; }
    if (action === 'properties') { setOpenDialog('properties'); }
  };

  const filteredItems = useMemo(() => {
    if (!query.trim()) return items;
    const term = query.trim().toLowerCase();
    return items.filter((item) => item.name.toLowerCase().includes(term));
  }, [items, query]);

  const uploadsInFlight = tasks.filter((t) => t.status === 'pending' || t.status === 'uploading');

  // Breadcrumb segments
  const pathSegments = currentPath ? currentPath.split('/').filter(Boolean) : [];

  // ─── Files View ──────────────────────────────────────────────
  const renderFilesView = () => {
    if (!activeConnection) {
      return (
        <Alert severity="info" className="glass-card" sx={{ borderRadius: 2 }}>
          No storage is available for this organization. Select an assigned storage
          {isPlatformOwner ? ' or add a connection from Admin.' : '.'}
        </Alert>
      );
    }
    return (
      <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flex: 1,
        minHeight: 360,
        borderRadius: 2,
        outline: isDraggingFiles
          ? `2px solid ${canWriteInCurrentPath ? '#8ff5ff' : '#ff716c'}`
          : '2px solid transparent',
        outlineOffset: 4,
        transition: 'outline-color 120ms ease, background-color 120ms ease',
      }}
    >
      {isDraggingFiles && (
        <Box
          data-testid="drive-drop-overlay"
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            display: 'grid',
            placeItems: 'center',
            pointerEvents: 'none',
            borderRadius: 2,
            bgcolor: canWriteInCurrentPath ? 'rgba(143,245,255,0.12)' : 'rgba(255,113,108,0.12)',
            border: `1px dashed ${canWriteInCurrentPath ? 'rgba(143,245,255,0.85)' : 'rgba(255,113,108,0.85)'}`,
            backdropFilter: 'blur(2px)',
          }}
        >
          <Stack spacing={1} alignItems="center">
            <UploadFileRoundedIcon sx={{ fontSize: 34, color: canWriteInCurrentPath ? '#8ff5ff' : '#ff716c' }} />
            <Typography sx={{ fontWeight: 700, color: canWriteInCurrentPath ? '#8ff5ff' : '#ff716c' }}>
              {canWriteInCurrentPath ? 'Drop files or folders to upload' : 'Upload access required'}
            </Typography>
            {canWriteInCurrentPath && (
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                Folder structure will be preserved
              </Typography>
            )}
            <Typography sx={{ fontSize: 12, color: 'text.secondary', maxWidth: 360, textAlign: 'center' }} noWrap>
              /{currentPath || activeConnection?.nickname || 'Drive'}
            </Typography>
          </Stack>
        </Box>
      )}
      <Stack spacing={2} sx={{ flex: 1, opacity: isDraggingFiles ? 0.45 : 1, transition: 'opacity 120ms ease' }}>
      {/* Toolbar */}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {currentPath && (
          <Button
            size="small"
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => { void loadPath(getParentPath(currentPath)); }}
            sx={{ color: 'text.secondary' }}
          >
            Back
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="outlined"
          startIcon={<PhotoLibraryRoundedIcon />}
          onClick={() => {
            void openFolderViewer(
              currentPath,
              currentPath.split('/').filter(Boolean).pop() || activeConnection?.nickname || 'Drive',
              'gallery',
            );
          }}
        >
          Gallery
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<PictureAsPdfRoundedIcon />}
          onClick={() => {
            void openFolderViewer(
              currentPath,
              currentPath.split('/').filter(Boolean).pop() || activeConnection?.nickname || 'Drive',
              'pdf-library',
            );
          }}
        >
          Library
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<IosShareRoundedIcon />}
          disabled={!canReadInCurrentPath}
          onClick={handleShareCurrentFolder}
        >
          Share
        </Button>
        <Button
          size="small"
          startIcon={<RefreshRoundedIcon />}
          onClick={() => { void loadPath(currentPath); }}
        >
          Refresh
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<CreateNewFolderRoundedIcon />}
          disabled={!canWriteInCurrentPath}
          onClick={() => setCreateFolderOpen(true)}
        >
          New folder
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<UploadFileRoundedIcon />}
          onClick={handleUploadClick}
          disabled={!canWriteInCurrentPath}

          sx={{ bgcolor: '#8ff5ff', color: '#003f43', '&:hover': { bgcolor: '#69edf9' } }}
        >
          Upload
        </Button>
      </Stack>

      {/* Upload progress (inline, only when active) */}
      {uploadsInFlight.length > 0 && (
        <Card className="glass-card" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Typography className="font-label" sx={{ fontSize: 10, color: '#8ff5ff', mb: 1 }}>
              Uploading {uploadsInFlight.length} file{uploadsInFlight.length > 1 ? 's' : ''}
            </Typography>
            {uploadsInFlight.map((task) => (
              <Stack key={task.id} direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: 13 }} noWrap>{task.file.name}</Typography>
                <Typography className="font-label" sx={{ fontSize: 10, color: 'text.secondary' }}>
                  {Math.round(task.progress)}%
                </Typography>
              </Stack>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!loading && filteredItems.length === 0 && (
        query ? (
          <Alert severity="info" className="glass-card" sx={{ borderRadius: 2 }}>
            No files match your search.
          </Alert>
        ) : canWriteInCurrentPath ? (
          <Box
            data-testid="drive-empty-drop-callout"
            role="note"
            aria-label="Folder upload supported"
            sx={{
              flex: 1,
              minHeight: { xs: 190, sm: 260 },
              display: 'grid',
              placeItems: 'center',
              px: 3,
              py: 4,
              borderRadius: 2,
              border: '1.5px dashed rgba(143,245,255,0.42)',
              bgcolor: 'rgba(143,245,255,0.035)',
              textAlign: 'center',
            }}
          >
            <Stack spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 58,
                  height: 58,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: '50%',
                  bgcolor: 'rgba(143,245,255,0.1)',
                  border: '1px solid rgba(143,245,255,0.2)',
                }}
              >
                <UploadFileRoundedIcon sx={{ fontSize: 30, color: '#8ff5ff' }} />
              </Box>
              <Typography sx={{ fontSize: 17, fontWeight: 750, color: 'text.primary' }}>
                Drag &amp; drop files or folders here
              </Typography>
              <Typography sx={{ maxWidth: 460, fontSize: 13, color: 'text.secondary' }}>
                Upload to this server folder while preserving the complete folder structure.
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<UploadFileRoundedIcon />}
                onClick={handleUploadClick}
                sx={{ mt: 0.5 }}
              >
                Browse files
              </Button>
            </Stack>
          </Box>
        ) : (
          <Alert severity="info" className="glass-card" sx={{ borderRadius: 2 }}>
            No files found in this folder.
          </Alert>
        )
      )}

      {/* Bulk action toolbar — appears when multi-select active */}
      <BulkActionToolbar
        count={selected.size}
        busy={actionBusy}
        onMove={() => setOpenBulkDialog('move')}
        onDelete={() => setOpenBulkDialog('delete')}
        onCancel={clearSelection}
      />

      {/* File list */}
      {filteredItems.length > 0 && (
        <Stack spacing={1}>
          {filteredItems.map((item) => {
            const isSelected = selected.has(item.path);
            const canReadItem = connectionCanAccess(activeConnection, hasFullDriveAccess, item.path, 'read');
            const canDeleteItem = connectionCanAccess(activeConnection, hasFullDriveAccess, item.path, 'delete');
            const canRenameItem = canDeleteItem && connectionCanAccess(activeConnection, hasFullDriveAccess, getParentPath(item.path), 'write');
            const canMoveItem = canDeleteItem && canWriteSomewhere;
            return (
              <DriveItemRow
                key={item.path}
                item={item}
                selected={isSelected}
                selectionEnabled={hasFullDriveAccess}
                canPreview={canReadItem}
                canShare={canReadItem && (item.type === 'folder' || isPdfPath(item.path))}
                canLibrary={item.type === 'folder' && canReadItem}
                canDownload={item.type === 'file' && canReadItem}
                canRename={canRenameItem}
                canMove={canMoveItem}
                canCopyUrl={canReadItem}
                canViewProperties={item.type === 'file' && canReadItem}
                canDelete={canDeleteItem}
                busy={actionBusy}
                onOpenFolder={(folder) => {
                  if (folder.type !== 'folder') return;
                  clearSelection();
                  void loadPath(folder.path);
                }}
                onToggleSelection={(target) => toggleSelected(target.path)}
                onPreview={(target) => {
                  if (target.type === 'folder') {
                    void openFolderViewer(target.path, target.name, 'gallery');
                  } else if (isPdfPath(target.path)) {
                    void handleOpenPdf(target.path);
                  }
                }}
                onShare={(target) => handleOpenEmbedDialog(
                  target,
                  target.type === 'file' ? 'pdf-reader' : undefined,
                )}
                onLibrary={(target) => {
                  if (target.type === 'folder') {
                    void openFolderViewer(target.path, target.name, 'pdf-library');
                  }
                }}
                onSecondaryAction={handleItemAction}
              />
            );
          })}
        </Stack>
      )}
      </Stack>
      </Box>
    );
  };

  // ─── Bookmarks View ──────────────────────────────────────────
  const renderBookmarksView = () => (
    <Stack spacing={3}>
      <Stack direction="row" spacing={1.5}>
        {isPlatformOwner && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => { setEditingConnection(null); setDialogOpen(true); }}
            sx={{ bgcolor: '#8ff5ff', color: '#003f43', '&:hover': { bgcolor: '#69edf9' } }}
          >
            Add Connection
          </Button>
        )}
      </Stack>

      {connections.length === 0 ? (
        <Alert severity="info" className="glass-card" sx={{ borderRadius: 2 }}>
          {isPlatformOwner
            ? 'No server connections yet. Add one to assign storage access.'
            : 'No connections are assigned to your account yet.'}
        </Alert>
      ) : (
        <Stack spacing={1.5}>
          {connections.map((connection) => {
            const isActive = activeConnection?.id === connection.id;
            return (
              <Card
                key={connection.id}
                className="glass-card"
                sx={{
                  borderRadius: 2,
                  borderColor: isActive ? '#8ff5ff' : undefined,
                  boxShadow: isActive ? '0 0 0 1px #8ff5ff inset' : undefined,
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography sx={{ fontWeight: 700 }}>{connection.nickname}</Typography>
                        <Chip size="small" label={PROTOCOL_LABELS[connection.protocol]} sx={{ fontSize: 10 }} />
                        {isActive && <Chip size="small" label="Active" sx={{ fontSize: 10, bgcolor: 'rgba(143,245,255,0.14)', color: '#8ff5ff' }} />}
                      </Stack>
                      <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5 }} noWrap>
                        {connection.endpoint}
                      </Typography>
                      {/* Public file status: shows when publicUrl is set so
                          the admin can see the file host is reachable and
                          CORS-permitted from the Box admin origin. */}
                      {connection.publicUrl && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                          {(() => {
                            const health = viewerHealthMap[connection.id];
                            if (!health || health === 'unchecked') {
                              return <Chip size="small" label="Checking CORS..." sx={{ fontSize: 10, bgcolor: 'rgba(255,255,255,0.06)', color: 'text.secondary' }} />;
                            }
                            if (health === 'ok') {
                              return <Chip size="small" label="View CORS reachable" sx={{ fontSize: 10, bgcolor: 'rgba(94,186,125,0.14)', color: '#5EBA7D' }} />;
                            }
                            if (health === 'cors-blocked') {
                              return <Chip size="small" label="CORS ✗ — see Setup Guide" sx={{ fontSize: 10, bgcolor: 'rgba(248,113,113,0.14)', color: '#F87171' }} />;
                            }
                            return <Chip size="small" label="File host unreachable" sx={{ fontSize: 10, bgcolor: 'rgba(248,113,113,0.14)', color: '#F87171' }} />;
                          })()}
                          <Typography
                            component="a"
                            href={connection.publicUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontSize: 11, color: '#8ff5ff', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            noWrap
                          >
                            {connection.publicUrl} ↗
                          </Typography>
                        </Stack>
                      )}
                      {hasFullDriveAccess && connection.protocol !== 'sftp' && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<UploadFileRoundedIcon />}
                          disabled={corsConfiguringId === connection.id}
                          onClick={() => { void handleConfigureCors(connection); }}
                          sx={{ minHeight: 28, mt: 1, fontSize: 11, px: 1 }}
                        >
                          {corsConfiguringId === connection.id ? 'Configuring...' : 'Enable browser upload'}
                        </Button>
                      )}
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      <Button size="small" onClick={() => handleOpenConnection(connection)}>
                        Open
                      </Button>
                      {isPlatformOwner && (
                        <>
                          <IconButton size="small" onClick={() => { setEditingConnection(connection); setDialogOpen(true); }}>
                            <DriveFileRenameOutlineIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => { void handleDeleteConnection(connection); }}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Stack>
  );

  // ─── Transfers View ──────────────────────────────────────────
  const renderTransfersView = () => (
    <Stack spacing={2}>
      {tasks.length === 0 ? (
        <Alert severity="info" className="glass-card" sx={{ borderRadius: 2 }}>
          No transfers yet. Upload a file to see activity here.
        </Alert>
      ) : (
        <Stack spacing={1}>
          {tasks.map((task) => (
            <Card key={task.id} className="glass-card" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 600 }} noWrap>{task.file.name}</Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>{task.targetPath}</Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography className="font-label" sx={{ fontSize: 10, color: task.status === 'failed' ? '#ff716c' : '#8ff5ff' }}>
                      {task.status}
                    </Typography>
                    <Typography className="font-label" sx={{ fontSize: 10, color: 'text.secondary' }}>
                      {Math.round(task.progress)}%
                    </Typography>
                  </Stack>
                </Stack>
                <Box sx={{ height: 3, borderRadius: 999, bgcolor: 'rgba(143,245,255,0.08)', overflow: 'hidden', mt: 1 }}>
                  <Box sx={{ width: `${Math.max(3, task.progress)}%`, height: '100%', bgcolor: task.status === 'failed' ? '#ff716c' : '#8ff5ff' }} />
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  );

  const renderAdminView = () => <AdminPanel />;

  const mainContent = (() => {
    if (activeView === 'admin') return renderAdminView();
    if (activeView === 'bookmarks') return renderBookmarksView();
    if (activeView === 'transfers') return renderTransfersView();
    return renderFilesView();
  })();

  const viewTabs: { key: DriveView; label: string }[] = [
    { key: 'files', label: 'Files' },
    { key: 'bookmarks', label: 'Connections' },
    { key: 'transfers', label: 'Transfers' },
    ...(canManageMembers ? [{ key: 'admin' as DriveView, label: 'Admin' }] : []),
  ];

  if (isApprovalPending) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2 }}>
        <Card className="glass-card" sx={{ maxWidth: 460, width: '100%', borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>Approval pending</Typography>
            <Typography sx={{ color: 'text.secondary', mb: 3 }}>
              Your request has been sent to a GenesisBox admin. This page checks for approval automatically, so you can leave it open.
            </Typography>
            {approvalRefreshError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {approvalRefreshError}
              </Alert>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button
                variant="contained"
                startIcon={<RefreshRoundedIcon />}
                disabled={approvalRefreshBusy}
                onClick={() => { void checkApprovalStatus(true); }}
              >
                {approvalRefreshBusy ? 'Checking…' : 'Check approval'}
              </Button>
              <Button variant="outlined" onClick={logout}>Sign out</Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'transparent', color: 'text.primary' }}>
      <ConnectionDialog
        open={dialogOpen}
        initialConnection={editingConnection}
        onClose={() => { setDialogOpen(false); setEditingConnection(null); }}
        onSave={handleSaveConnection}
      />
      <EmbedDialog
        open={embedDialogOpen}
        target={embedTarget}
        shares={embedShares}
        publicUrl={activeConnection?.publicUrl}
        connectionPrefix={activeConnection?.prefix}
        storageScope={activeStorageScope}
        onClose={() => { setEmbedDialogOpen(false); setEmbedTarget(null); }}
        onSharesChanged={refreshEmbedShares}
      />
      <CreateFolderDialog
        open={createFolderOpen}
        currentPath={currentPath}
        busy={actionBusy}
        onCancel={() => setCreateFolderOpen(false)}
        onConfirm={(folderName) => { void handleCreateFolder(folderName); }}
      />

      {/* Drive item action dialogs — single-item flows */}
      <RenameDialog
        open={openDialog === 'rename'}
        sourcePath={actionTarget?.path || ''}
        isFolder={actionTarget?.type === 'folder'}
        busy={actionBusy}
        onCancel={() => { setOpenDialog(null); setActionTarget(null); }}
        onConfirm={(newPath) => { void handleSingleRename(newPath); }}
      />
      <MoveDialog
        open={openDialog === 'move'}
        sources={actionTarget ? [actionTarget.path] : []}
        initialTarget={currentPath}
        busy={actionBusy}
        onCancel={() => { setOpenDialog(null); setActionTarget(null); }}
        onConfirm={(targetFolder) => { void handleSingleMove(targetFolder); }}
      />
      <DeleteConfirmDialog
        open={openDialog === 'delete'}
        count={1}
        itemName={actionTarget?.name}
        busy={actionBusy}
        onCancel={() => { setOpenDialog(null); setActionTarget(null); }}
        onConfirm={() => { void handleSingleDelete(); }}
      />
      <FilePropertiesDialog
        open={openDialog === 'properties'}
        path={actionTarget?.path || ''}
        size={actionTarget && isFile(actionTarget) ? actionTarget.size : undefined}
        lastModified={actionTarget && isFile(actionTarget) ? actionTarget.lastModified : undefined}
        onClose={() => { setOpenDialog(null); setActionTarget(null); }}
      />

      {/* Bulk dialogs — N-item flows */}
      <DeleteConfirmDialog
        open={openBulkDialog === 'delete'}
        count={selected.size}
        busy={actionBusy}
        onCancel={() => setOpenBulkDialog(null)}
        onConfirm={() => { void handleBulkDelete(); }}
      />
      <MoveDialog
        open={openBulkDialog === 'move'}
        sources={[...selected]}
        initialTarget={currentPath}
        busy={actionBusy}
        onCancel={() => setOpenBulkDialog(null)}
        onConfirm={(targetFolder) => { void handleBulkMove(targetFolder); }}
      />

      {/* Toast for action results — auto-dismisses 4s. */}
      <Snackbar
        open={!!actionToast}
        autoHideDuration={4000}
        onClose={() => setActionToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {actionToast ? (
          <Alert severity={actionToast.severity} onClose={() => setActionToast(null)} sx={{ borderRadius: 2 }}>
            {actionToast.message}
          </Alert>
        ) : undefined}
      </Snackbar>

      {/* Inline ViewerPanel Drawer (PR 2 — owner fast-path).
          Mounts only when openFolderViewer set inlineViewer state.
          Drawer slides from right at md+, becomes full-screen Dialog under sm. */}
      <Drawer
        anchor={isMobileDrawer ? 'bottom' : 'right'}
        open={!!inlineViewer}
        onClose={() => setInlineViewer(null)}
        keepMounted={false}
        PaperProps={{
          sx: {
            width: isMobileDrawer ? '100vw' : { xs: '100vw', sm: '80vw', md: '70vw' },
            height: isMobileDrawer ? '100vh' : '100%',
            backgroundColor: '#202124',
            color: 'rgba(255,255,255,0.95)',
            borderRadius: 0,
          },
        }}
      >
        {inlineViewer && (
          <ViewerPanel
            auth={{ kind: 'owner' }}
            folderPath={inlineViewer.folderPath}
            filePath={inlineViewer.filePath}
            title={inlineViewer.folderName}
            contentType={inlineViewer.contentType}
            initialMode={inlineViewer.initialMode}
            onClose={() => setInlineViewer(null)}
            getEmbedInfo={(mode, state) => getFolderEmbedInfo({
              folderPath: inlineViewer.folderPath,
              folderName: inlineViewer.folderName,
              contentType: inlineViewer.contentType,
              mode,
              state,
            })}
          />
        )}
      </Drawer>
      <input ref={fileInputRef} type="file" hidden multiple onChange={(e) => { void handleUploadChange(e); }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* ─── Header ─────────────────────────────────────────── */}
        <Box
          component="header"
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            px: { xs: 2, md: 3 },
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            flexWrap: { xs: 'wrap', lg: 'nowrap' },
            columnGap: { xs: 1, lg: 2 },
            rowGap: 1,
            bgcolor: 'rgba(10,10,12,0.85)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(118,117,119,0.12)',
          }}
        >
          <Box
            component="img"
            src="/icon-192.png?v=4"
            alt="GenesisBox"
            sx={{
              width: { xs: 38, sm: 42 },
              height: { xs: 38, sm: 42 },
              minWidth: 38,
              minHeight: 38,
              borderRadius: 1.75,
              objectFit: 'cover',
              flexShrink: 0,
              border: '1px solid rgba(143,245,255,0.18)',
            }}
          />
          {/* Brand — shows the active connection beside the GenesisBox avatar. */}
          <Typography
            sx={{
              fontSize: activeConnection ? 14 : 18,
              fontWeight: 900,
              color: '#8ff5ff',
              letterSpacing: '-0.04em',
              mr: 1,
              display: { xs: 'none', lg: 'block' },
              maxWidth: 160,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={activeConnection?.nickname}
          >
            {activeConnection?.nickname || 'GenesisBox'}
          </Typography>

          {showHeaderSwitcher && (
            <TextField
              select
              size="small"
              value={headerSelectValue}
              onChange={(event) => { handleHeaderSelect(event.target.value); }}
              sx={{
                order: 0,
                minWidth: { xs: 0, lg: 190 },
                maxWidth: { xs: 'none', lg: 260 },
                flex: { xs: '1 1 120px', lg: '0 1 260px' },
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(19,19,21,0.92)',
                  borderRadius: 1.5,
                  fontSize: 13,
                  height: 34,
                },
              }}
              SelectProps={{ inputProps: { 'aria-label': 'Storage' } }}
            >
              {/* Each org is a selectable row; its storages list beneath it as
                  "Org · Storage". Storages only load for the active org, so
                  other orgs show just their name until selected. The current
                  organization row is disabled so it can never clear the
                  selected storage. Flattened
                  arrays because MUI <Select> reads MenuItem values from its
                  direct children. */}
              {orgOptions.flatMap((option) => {
                const isCurrent = option.id === currentOrgId;
                const orgStorages = isCurrent ? connections : [];
                return [
                  <MenuItem
                    key={`org:${option.id}`}
                    value={`org:${option.id}`}
                    disabled={isCurrent}
                    sx={{ fontWeight: 600 }}
                  >
                    {option.name}
                  </MenuItem>,
                  ...orgStorages.map((connection) => (
                    <MenuItem key={`conn:${connection.id}`} value={`conn:${connection.id}`} sx={{ pl: 3, fontSize: 12.5 }}>
                      {option.name} · {connection.nickname}
                    </MenuItem>
                  )),
                ];
              })}
            </TextField>
          )}

          {/* Tabs */}
          <Stack
            direction="row"
            spacing={{ xs: 0, sm: 0.5 }}
            sx={{
              order: { xs: 1, lg: 0 },
              minWidth: 0,
              width: { xs: '100%', lg: 'auto' },
              flexShrink: 1,
              overflowX: 'auto',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {viewTabs.map((tab) => (
              <Button
                key={tab.key}
                size="small"
                onClick={() => setActiveView(tab.key)}
                sx={{
                  px: { xs: 0.75, sm: 1.5 },
                  minWidth: 0,
                  color: activeView === tab.key ? '#8ff5ff' : '#7d8391',
                  borderBottom: '2px solid',
                  borderColor: activeView === tab.key ? '#8ff5ff' : 'transparent',
                  borderRadius: 0,
                  fontSize: { xs: 11, sm: 13 },
                  fontWeight: activeView === tab.key ? 700 : 400,
                }}
              >
                {tab.label}
                {tab.key === 'transfers' && uploadsInFlight.length > 0 && (
                  <Box sx={{ ml: 0.75, width: 6, height: 6, borderRadius: '50%', bgcolor: '#8ff5ff' }} />
                )}
              </Button>
            ))}
          </Stack>

          <Box sx={{ flex: 1, display: { xs: 'none', lg: 'block' } }} />

          {/* Search */}
          <TextField
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search..."
            size="small"
            sx={{
              order: { xs: 2, lg: 0 },
              display: 'flex',
              width: { xs: '100%', lg: 260 },
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(19,19,21,0.92)',
                borderRadius: 999,
                fontSize: 13,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />

          {/* User + Logout */}
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ order: 0, ml: { xs: 'auto', lg: 0 } }}
          >
            <Avatar sx={{ display: { xs: 'none', sm: 'flex' }, width: 30, height: 30, bgcolor: 'rgba(143,245,255,0.12)', color: '#8ff5ff', fontSize: 14, border: '1px solid rgba(143,245,255,0.18)' }}>
              {(user || 'G').slice(0, 1).toUpperCase()}
            </Avatar>
            <Tooltip title="Sign out">
              <IconButton size="small" onClick={logout} sx={{ color: 'text.secondary' }}>
                <LogoutRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* ─── Path Bar ────────────────────────────────────────── */}
        {activeView === 'files' && (
          <Box sx={{ px: { xs: 2, md: 3 }, pt: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: 'rgba(19,19,21,0.92)',
                border: '1px solid rgba(143,245,255,0.10)',
                borderRadius: 1.5,
                px: 1.5,
                py: 0.75,
                overflow: 'hidden',
              }}
            >
              <Stack direction="row" spacing={0} alignItems="center" sx={{ minWidth: 0, overflow: 'auto', '&::-webkit-scrollbar': { display: 'none' } }}>
                <Button
                  size="small"
                  sx={{
                    minWidth: 0,
                    px: 0.75,
                    color: pathSegments.length === 0 ? '#8ff5ff' : 'rgba(143,245,255,0.5)',
                    fontSize: 13,
                    fontFamily: 'monospace',
                    textTransform: 'none',
                    '&:hover': { color: '#8ff5ff' },
                  }}
                  onClick={() => { void loadPath(''); }}
                >
                  /
                </Button>
                {pathSegments.map((segment, i) => {
                  const segPath = pathSegments.slice(0, i + 1).join('/');
                  const isLast = i === pathSegments.length - 1;
                  return (
                    <Box key={segPath} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Button
                        size="small"
                        sx={{
                          minWidth: 0,
                          px: 0.75,
                          color: isLast ? '#8ff5ff' : 'rgba(143,245,255,0.5)',
                          fontSize: 13,
                          fontFamily: 'monospace',
                          textTransform: 'none',
                          whiteSpace: 'nowrap',
                          '&:hover': { color: '#8ff5ff' },
                        }}
                        onClick={() => { if (!isLast) void loadPath(segPath); }}
                      >
                        {segment}
                      </Button>
                      <Typography sx={{ fontSize: 13, fontFamily: 'monospace', color: 'rgba(143,245,255,0.25)', userSelect: 'none' }}>/</Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Box>
        )}

        {/* ─── Main Content ───────────────────────────────────── */}
        <Box
          component="main"
          data-testid={activeView === 'files' ? 'drive-drop-zone' : undefined}
          onDragEnter={activeView === 'files' && activeConnection ? handleFileDragEnter : undefined}
          onDragOver={activeView === 'files' && activeConnection ? handleFileDragOver : undefined}
          onDragLeave={activeView === 'files' && activeConnection ? handleFileDragLeave : undefined}
          onDrop={activeView === 'files' && activeConnection
            ? (event) => { void handleFileDrop(event); }
            : undefined}
          sx={{
            position: 'relative',
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            px: { xs: 2, md: 3 },
            py: 2,
          }}
        >
          {error && activeView === 'files' && (
            <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{error}</Alert>
          )}
          {viewerError && activeView === 'files' && (
            <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }} onClose={() => setViewerError(null)}>
              {viewerError}
            </Alert>
          )}

          {loading && activeView === 'files' ? (
            <Alert severity="info" className="glass-card" sx={{ borderRadius: 2 }}>
              Loading files...
            </Alert>
          ) : mainContent}
        </Box>
      </Box>
    </Box>
  );
}
